<script lang="ts">
	import { format } from 'svelte-i18n';
	//Svelte hooks
	import { beforeUpdate } from 'svelte';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import {
		device_notifications,
		dismissed_notifications,
	} from 'src/stores/features/device-notifications';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { dismissNotification } from 'src/helpers/notifications/device_notifications_helper';
	//Design system components
	import { Alert, Stack, Text } from '@Shelly/shelly-design-system';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { defaultStateType } from 'src/stores/device-runtime/states';
	import DeviceStateSlot from 'src/components/Lists/Devices/DeviceStateSlot.svelte';
	//Types
	import type { DeviceNotification } from 'src/helpers/notifications/DeviceNotification';

	export let devicesFilter = Object.keys($devices);
	export let timeToClear = 86400; // 24 hours to clear seen notifications by default

	$: devicesNotifications = $device_notifications;

	$: notificationsToShow = Object.fromEntries(
		[...Object.entries(devicesNotifications)].filter(([id]) => {
			return devicesFilter.includes(id?.split('-')[0]);
		}),
	) as { [category: string]: DeviceNotification };

	$: notificationsLength =
		Object.keys(notificationsToShow).length - Object.keys($dismissed_notifications).length;

	beforeUpdate(() => {
		const time = +(new Date().getTime() / 1000).toFixed(0);
		Object.entries($dismissed_notifications).forEach(([id, ts]) => {
			if (time - Number(ts) > timeToClear) {
				dismissed_notifications.remove(id);
			}
		});
	});
</script>

{#if notificationsLength > 0 || (notificationsLength !== Object.keys($dismissed_notifications).length && notificationsLength > 0)}
	<Text element="h2" class="mb-s">
		{capFirstChar($format('settings_title_notifications', { default: 'Notifications' }))}
		({notificationsLength})
	</Text>
	<Stack gap="xs" class="mb-s">
		{#each Object.values(notificationsToShow) as { type, heading, id, ctaGlobal, message, messageHighlight, error, dismissable }}
			{@const device = $devices[id?.split('-')[0]]}
			<DeviceStateSlot deviceId={device?.id} let:deviceState>
				{@const isDisabled = deviceState?.type < defaultStateType}
				<Alert
					type={type === 'promo' ? 'info' : type}
					heading={heading?.global}
					dismissable={dismissable ?? true}
					{...error ? { customIcon: 'text-warning fas fa-shield-exclamation' } : {}}
					on:dismissable_click={() => dismissNotification(id)}
					class={typeof $dismissed_notifications[id] !== 'undefined' ? 'dismissable' : ''}
					isHeadingBold={!!message}
					hideSlotBottom={!ctaGlobal?.title && !message}
				>
					<svelte:fragment slot="bottom">
						{#if message}
							<Text
								class={classMap({
									'mb-s': ctaGlobal?.title && !isDisabled,
								})}
								styleType="body-small"
							>
								<span class="text-bold">{device?.name}</span>
								{#if messageHighlight}
									{' '}<span class="text-bold">{messageHighlight}</span>{' '}
								{/if}
								{message}
							</Text>
						{/if}
						{#if ctaGlobal?.title}
							<button
								class="reset-button-styles-base link-styles d-flex align-items-start"
								style="
								font-size: 13px;
								line-height: 16px;
							"
								type="button"
								on:click={(event) => {
									event.stopPropagation();
									ctaGlobal?.onClick();
								}}
							>
								{ctaGlobal?.title}
							</button>
						{/if}
					</svelte:fragment>
				</Alert>
			</DeviceStateSlot>
		{/each}
	</Stack>
{:else}
	<Text element="h2" class="mb-s">
		{capFirstChar($format('no_notifications_msg', { default: 'No Notifications' }))}.
	</Text>

	<Text class="mb-s" styleType="body-small">
		{$format('new_notifications_desc', {
			default: "When something new arrives, you'll see it here.",
		})}</Text
	>
{/if}
